import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useDispatch } from 'react-redux'
import { Button, Tooltip } from '@diversioteam/diversio-ds'

import * as S from './ImpersonationBanner.styles'

import { useImpersonationStatus } from '@/hooks/impersonation/useImpersonationStatus'
import { optimoPrivateApi } from '@/api/axios/axiosInstance'
import { endpoints } from '@/api/endpoints'
import { setTokensWithFallback } from '@/utils/auth/tokenHelpers'
import { clearImpersonationFlag } from '@/utils/auth/impersonationSession'
import { resetQueryCacheOnImpersonationEnd } from '@/utils/reactQuery/cacheReset'
import { setAuthData } from '@/store/slices/authSlice'
import type { ExchangeTokenResponse } from '@/api/actions/auth/authActions.types'

export function ImpersonationBanner() {
    const { status } = useImpersonationStatus()
    const [loading, setLoading] = useState(false)
    const dispatch = useDispatch()
    const queryClient = useQueryClient()

    const isApi = status?.access_method === 'api'
    const scopeLabel = status?.scope === 'write' ? 'Write' : 'Read-only'

    // Countdown to expiry (mm:ss) - optimized with visibility check
    const [now, setNow] = useState<number>(() => Date.now())
    const rafRef = useRef<number | null>(null)
    const lastUpdateRef = useRef<number>(0)
    const expiresAt = useMemo(
        () => (status?.session_info?.expires ? Date.parse(status.session_info.expires) : null),
        [status],
    )

    useEffect(() => {
        if (!expiresAt) return () => {}

        const updateTimer = (timestamp: number) => {
            // Only update once per second and when document is visible
            if (document.hidden || timestamp - lastUpdateRef.current < 1000) {
                rafRef.current = requestAnimationFrame(updateTimer)
                return
            }

            lastUpdateRef.current = timestamp
            setNow(Date.now())
            rafRef.current = requestAnimationFrame(updateTimer)
        }

        rafRef.current = requestAnimationFrame(updateTimer)

        return () => {
            if (rafRef.current) cancelAnimationFrame(rafRef.current)
        }
    }, [expiresAt])

    const remaining = useMemo(() => {
        if (!expiresAt) return null
        const diff = Math.max(0, Math.floor((expiresAt - now) / 1000))
        const m = Math.floor(diff / 60)
        const s = String(diff % 60).padStart(2, '0')
        return `${m}m ${s}s`
    }, [expiresAt, now])

    const handleEnd = async () => {
        if (!isApi || loading) return
        setLoading(true)
        try {
            const { data } = await optimoPrivateApi.post<ExchangeTokenResponse>(endpoints.impersonation.end)
            if (data?.access_token) {
                // API flow returns fresh tokens for the original user
                setTokensWithFallback(data.access_token, data.refresh_token)
                dispatch(
                    setAuthData({
                        access_token: data.access_token,
                        refresh_token: data.refresh_token ?? data.access_token,
                        expires_at: data.expires_at,
                        refresh_expires_at: data.refresh_expires_at ?? data.expires_at,
                        user: data.user,
                    }),
                )
                // Clear impersonation flag and wipe any cached data from the impersonated user
                // to avoid stale/incorrect views after switching back to admin.
                clearImpersonationFlag()
                await resetQueryCacheOnImpersonationEnd(queryClient)
            }
        } catch {
            // ignore for now; the header guard will naturally reject old token
        } finally {
            setLoading(false)
        }
    }

    if (!status?.is_impersonating) return null

    return (
        <Tooltip title="Logout ends this session" content="Logout ends this session" variant="light">
            <S.BannerContainer aria-live="polite" aria-atomic="true">
                <S.BannerText>
                    <strong>Impersonating</strong>
                    <span>
                        {remaining && (
                            <>
                                <span>· </span>
                                <S.TimerText>{remaining}</S.TimerText>
                            </>
                        )}
                        {status.scope && remaining && <span> · </span>}
                        {status.scope && <span>{scopeLabel}</span>}
                    </span>
                </S.BannerText>
                <S.Actions>
                    {isApi && (
                        <Button color="primary" size="small" onClick={handleEnd} disabled={loading}>
                            {loading ? 'Ending…' : 'End Impersonation'}
                        </Button>
                    )}
                </S.Actions>
            </S.BannerContainer>
        </Tooltip>
    )
}
